const asString = (value) => {
  if (value == null) return ''
  return `${value}`.trim()
}

const clone = (value) => {
  if (Array.isArray(value)) {
    return value.map(item => clone(item))
  }
  if (value && typeof value === 'object') {
    return JSON.parse(JSON.stringify(value))
  }
  return value
}

export const normalizeRuleKey = (value) => {
  const normalizedValue = asString(value).toUpperCase()
  if (normalizedValue === 'MATERIAL_FIXED_FIELD') return 'MATERIAL_FIXED_FIELDS'
  if (normalizedValue === 'RESOURCE_FIXED_FIELD') return 'RESOURCE_FIXED_FIELDS'
  return normalizedValue
}

const readValueByPath = (value, path) => {
  if (value == null || !path) return value
  if (typeof path !== 'string') return value[path]

  return path.split('.').reduce((acc, key) => {
    if (acc == null) return undefined
    return acc[key]
  }, value)
}

const getConditionExpectedValue = (condition) => {
  if (!condition) return undefined
  if (condition.string_list != null) return condition.string_list
  if (condition.int_list != null) return condition.int_list
  if (condition.bool_value != null) return condition.bool_value
  if (condition.date_value != null) return condition.date_value
  if (condition.int_value != null) return condition.int_value
  if (condition.string_value != null) return condition.string_value
  return undefined
}

const normalizeComparableValue = (value) => {
  if (Array.isArray(value)) {
    return value.map(item => normalizeComparableValue(item))
  }

  if (value instanceof Date) {
    return value.getTime()
  }

  if (value && typeof value === 'object') {
    if (Object.prototype.hasOwnProperty.call(value, 'id')) {
      return normalizeComparableValue(value.id)
    }
    if (Object.prototype.hasOwnProperty.call(value, 'value')) {
      return normalizeComparableValue(value.value)
    }
    if (Object.prototype.hasOwnProperty.call(value, 'name')) {
      return normalizeComparableValue(value.name)
    }
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed.length) return ''
    const numericValue = Number(trimmed)
    if (!Number.isNaN(numericValue) && `${numericValue}` === trimmed) {
      return numericValue
    }
    const dateValue = Date.parse(trimmed)
    if (!Number.isNaN(dateValue) && /\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      return dateValue
    }
    return trimmed
  }

  return value
}

const compareValues = (actual, expected, operator) => {
  const normalizedOperator = normalizeRuleKey(operator)

  if (normalizedOperator === 'EQUALS' || normalizedOperator === 'EQ' || normalizedOperator === '=') {
    return normalizeComparableValue(actual) === normalizeComparableValue(expected)
  }

  if (normalizedOperator === 'INCLUDES' || normalizedOperator === 'CONTAINS') {
    const normalizedActual = normalizeComparableValue(actual)
    const normalizedExpected = normalizeComparableValue(expected)

    if (Array.isArray(normalizedActual) && Array.isArray(normalizedExpected)) {
      return normalizedExpected.some(expectedItem => normalizedActual.includes(expectedItem))
    }

    if (Array.isArray(normalizedActual)) {
      return normalizedActual.some(actualItem => compareValues(actualItem, normalizedExpected, 'EQUALS'))
    }

    if (Array.isArray(normalizedExpected)) {
      return normalizedExpected.some(expectedItem => compareValues(normalizedActual, expectedItem, 'EQUALS'))
    }

    return asString(normalizedActual).toUpperCase().includes(asString(normalizedExpected).toUpperCase())
  }

  return false
}

export const shouldBypassValidationRule = (validation) => {
  const conditions = (Array.isArray(validation?.validation_groups) ? validation.validation_groups : [])
    .flatMap(group => (Array.isArray(group?.validation_conditions) ? group.validation_conditions : []))

  return conditions.some((condition) => {
    const source = normalizeRuleKey(condition?.source)
    return source === 'PLUGIN_FIXED_FIELDS' || source === 'PLUGIN_CUSTOM_FIELDS'
  })
}

const evaluateCondition = (condition, contexts) => {
  if (!condition) return false
  const sourceContext = contexts[normalizeRuleKey(condition.source)]
  if (sourceContext == null) return false
  const actualValue = readValueByPath(sourceContext, condition.left_operand)
  return compareValues(actualValue, getConditionExpectedValue(condition), condition.operator)
}

const evaluateGroup = (group, contexts) => {
  const conditions = Array.isArray(group?.validation_conditions) ? group.validation_conditions : []
  if (!conditions.length) return false

  const normalizedType = normalizeRuleKey(group?.type) === 'OR' ? 'OR' : 'AND'
  const results = conditions.map(condition => evaluateCondition(condition, contexts))
  return normalizedType === 'OR' ? results.some(Boolean) : results.every(Boolean)
}

export const evaluateValidationRule = (validation, contexts) => {
  const groups = Array.isArray(validation?.validation_groups)
    ? [...validation.validation_groups].sort((left, right) => (left?.sequence || 0) - (right?.sequence || 0))
    : []

  if (!groups.length) return false

  let accumulatedResult = evaluateGroup(groups[0], contexts)

  for (let index = 1; index < groups.length; index += 1) {
    const connector = normalizeRuleKey(groups[index - 1]?.operator_to_next_group) || 'AND'
    const nextResult = evaluateGroup(groups[index], contexts)
    accumulatedResult = connector === 'OR' ? accumulatedResult || nextResult : accumulatedResult && nextResult
  }

  return accumulatedResult
}

export const getActionMetadataValue = (value) => {
  if (value && typeof value === 'object') {
    if (Object.prototype.hasOwnProperty.call(value, 'id') && Object.prototype.hasOwnProperty.call(value, 'name')) {
      return clone(value)
    }
    if (Object.prototype.hasOwnProperty.call(value, 'value')) {
      return clone(value.value)
    }
  }
  return clone(value)
}

export const getActionFieldId = (field) => {
  if (!field) return null
  if (typeof field === 'string') return field
  return field.id || field.value || field.name || null
}
